import { getField, updateField } from "vuex-map-fields";
import Swal from "sweetalert2";
import router from "@/router";
import _ from "lodash";
import httpClient from "@/mixins/httpClient";

const getDefaultState = () => {
  return {
    startIndex: 0,
    creating: false,
    logoImage: null,
    agentImage: null,
    editDataSet: false,
    form: {
      abn: '',
      businessAddress: {
        flat: '',
        postcode: '',
        state: '',
        streetName: '',
        streetNumber: '',
        suburb: '',
        description: '',
        coordinates: { lat: null, lng: null },
        googleMapsPlaceEid: '',
      },
      businessName: '',
      contactEmail: '',
      contactName: '',
      contactPhone: '',
      paymentType: '',
      tradingName: '',
      tradingHours: {
        monday: undefined,
        tuesday: undefined,
        wednesday: undefined,
        thursday: undefined,
        friday: undefined,
        saturday: undefined,
        sunday: undefined,
        weekdays: undefined,
        weekends: undefined,
      },
      tradingHoursIndividualDays: false,
      websiteUrl: '',
      pongrassAccountEid: '',
      businessType: null,
      customerPrimaryAgeRanges: [],
      customerPrimaryGender: '',
      customerSecondaryAgeRanges: [],
      customerSecondaryGender: '',
      servicesDescription: '',
      servicesRadius: 5,
      servicesCoordinates: { lat: null, lng: null },
      servicesPostcode: '',
      marketingObjective: {
        buildBrandAwareness: false,
        enhanceCustomerEngagement: false,
        growMarketShare: false,
        increaseSales: false,
        launchNewProductOrService: false,
        targetNewCustomers: false,
      },
      marketingAction: {
        driveFootTraffic: false,
        drivePhoneCalls: false,
        driveWebTraffic: false
      },
      shortDescription: '',
      longDescription: '',
      logoImage: '',
      agentImage: '',
      backgroundColor: '#000000',
      customerAnnualBudget: '',
      typicalChannels: []
    }
  }
}

export const state = getDefaultState()

export const getters = {
  getField
};

export const mutations = {
  updateField,
  resetState (state) {
    Object.assign(state, getDefaultState());
  },
  setCreating(state, value) {
    state.creating = value
  },
  handleHoursChange(state, { date, type, day }) {
    state.form.tradingHours[day][type] = date
  },
  handleTradingHoursToggle(state, day) {
    if (state.form.tradingHours[day]) {
      state.form.tradingHours[day] = undefined
    } else {
      state.form.tradingHours = { ...state.form.tradingHours, [day]: { opening: null, closing: null } }
    }
  },
  handleTypicalChannelsSelect(state, typicalChannel) {
    state.form.typicalChannels.push(typicalChannel);
  },
  handleTypicalChannelsRemove(state, typicalChannel) {
    let index = state.form.typicalChannels.indexOf(typicalChannel);

    if (index !== -1) {
      state.form.typicalChannels.splice(index, 1);
    }
  },
  handleAddressData(state, { address_components, geometry, place_id }) {
    const addressData = {}
    address_components.forEach((component) => {       
      if (component.types[0] === 'administrative_area_level_1') {
        addressData[component.types[0]] = component.short_name;
      } else {
        addressData[component.types[0]] = component.long_name;
      }  
    });

    state.form.businessAddress.coordinates = geometry.location.toJSON();
    state.form.businessAddress.googleMapsPlaceEid = place_id;

    state.form.businessAddress.streetNumber = addressData.street_number;
    state.form.businessAddress.streetName = addressData.route;
    state.form.businessAddress.flat = addressData.subpremise;
    state.form.businessAddress.suburb = addressData.locality;
    state.form.businessAddress.postcode = addressData.postal_code;
    state.form.businessAddress.state = addressData.administrative_area_level_1;
  },
  handleAddressEdit: _.debounce((state) => {
    const { businessAddress } = state.form;

    httpClient.methods.apiGetPublic("https://maps.googleapis.com/maps/api/geocode/json", { address: `${businessAddress.streetNumber} ${businessAddress.streetName}, ${businessAddress.suburb} ${businessAddress.state} ${businessAddress.postcode}`, sensor: false, key: process.env.VUE_APP_GOOGLE_MAPS_API_KEY, components: `country:AU` }).then((data) => {
      const { status, results } = data;

      if (status === "OK") {
        state.form.businessAddress.coordinates.lat = results[0].geometry.location.lat;
        state.form.businessAddress.coordinates.lng = results[0].geometry.location.lng;
        state.form.businessAddress.googleMapsPlaceEid = results[0].place_id;
      }
    });
  }, 1000),
  setEditData(state, data) {
    Object.assign(state.form, data);

    delete state.form.id
    delete state.form.businessAddress.id

    Object.keys(state.form.tradingHours).forEach((key) => {
      if (!state.form.tradingHours[key].opening && !state.form.tradingHours[key].opening) {
        data.tradingHours[key] = undefined
      }
    });    

    delete state.form.marketingAction.id
    delete state.form.marketingObjective.id

    state.form.businessType = data.businessType.id
    state.form.customerPrimaryGender = data.customerPrimaryGender

    if (data.customerSecondaryGender) {
      state.form.customerSecondaryGender = data.customerSecondaryGender
    }

    if (data.agentImage) {
      state.agentImage = data.agentImage
      state.form.agentImage = data.agentImage.id
    }

    if (data.logoImage) {
      state.logoImage = data.logoImage
      state.form.logoImage = data.logoImage.id
    }

    delete state.form.createdAt
    delete state.form.deletedAt
    delete state.form.updatedAt

    state.editDataSet = true
  },
  setStartIndex(state, index) {
    state.startIndex = index
  }
};

export const actions = {
  create({ state, commit }, { status }) {
    commit('setCreating', true);
    return httpClient.methods.apiPost(`${process.env.VUE_APP_API_URL}/advertisers`, { ...state.form, status }).then(() => {
      Swal.fire({
        title: `You have successfully added ${state.form.businessName}`,
        icon: 'success',
        confirmButtonText: 'Back Home',
        confirmButtonColor: "#1D99C7",
        allowOutsideClick: false,
        allowEscapeKey: false
      }).then(result => {
        commit('setCreating', false);
        if (result.value) {
          commit('resetState');
          router.push({ path: '/advertisers' })
        }
      });

      return null;
    }).catch((error) => {
      commit('setCreating', false);
      return {
        errors: error.response ? error.response.data.error.message : null
      }
    });
  },
  update({state, commit }, { status, id }) {
    commit('setCreating', true);
    return httpClient.methods.apiPatch(`${process.env.VUE_APP_API_URL}/advertisers/${id}`, { ...state.form, status }).then(() => {
      Swal.fire({
        title: `You have successfully updated ${state.form.businessName}`,
        icon: 'success',
        confirmButtonText: 'Back Home',
        confirmButtonColor: "#1D99C7",
        allowOutsideClick: false,
        allowEscapeKey: false
      }).then(result => {
        commit('setCreating', false);
        if (result.value) {
          commit('resetState');
          router.push({ path: '/advertisers' });
        }
      });

      return null;
    }).catch((error) => {
      commit('setCreating', false);
      return {
        errors: error.response ? error.response.data.error.message : null
      }
    })
  }
}
